import type { Rect, V2 } from '../core/math';
import type { BodyTagData } from '../core/events';
import { Body, StaticCollider, computeGeom, makeGeom, type WorldGeom } from './body';
import { aabbOverlap, collide, makeManifold, type Manifold } from './collide';
import { SpatialGrid } from './grid';
import { rayShape, type RayShapeHit } from './raycast';
import {
  Layer,
  type BodyDef,
  type ContactEvent,
  type PhysicsStats,
  type RayHit,
  type RaycastOptions,
  type Shape,
  type StaticDef,
  type TriggerDef,
  type TriggerEvent,
} from './types';

export interface PhysicsConfig {
  /** Broadphase cell size in metres. */
  cellSize: number;
  /** Collision substeps per `step` call (2 = 120 Hz collision at 60 Hz sim). */
  substeps: number;
  velocityIterations: number;
  /** Allowed penetration before position correction kicks in (m). */
  slop: number;
  /** Fraction of penetration corrected per substep. */
  correction: number;
  /** Closing speed below which restitution is ignored (m/s). */
  restitutionThreshold: number;
  sleepLinear: number;
  sleepAngular: number;
  sleepTime: number;
}

export const DEFAULT_PHYSICS_CONFIG: PhysicsConfig = {
  cellSize: 8,
  substeps: 2,
  velocityIterations: 8,
  slop: 0.01,
  correction: 0.6,
  restitutionThreshold: 1,
  sleepLinear: 0.08,
  sleepAngular: 0.08,
  sleepTime: 0.6,
};

export class Trigger {
  readonly geom: WorldGeom = makeGeom();
  readonly inside = new Map<number, BodyTagData>();
  stamp = 0;
  constructor(
    readonly id: number,
    readonly shape: Shape,
    public pos: V2,
    public angle: number,
    public mask: number,
    public tag: string,
  ) {
    computeGeom(this.geom, shape, pos, angle);
  }
  /** Move the trigger (takes effect next step). */
  setPose(pos: V2, angle = this.angle): void {
    this.pos = { x: pos.x, z: pos.z };
    this.angle = angle;
    computeGeom(this.geom, this.shape, this.pos, this.angle);
  }
}

interface Constraint {
  a: Body;
  b: Body | null;
  s: StaticCollider | null;
  key: number;
  m: Manifold;
  friction: number;
  restitution: number;
  count: number;
  rAx: [number, number];
  rAz: [number, number];
  rBx: [number, number];
  rBz: [number, number];
  normalMass: [number, number];
  tangentMass: [number, number];
  velBias: [number, number];
  pn: [number, number];
  pt: [number, number];
  approach: number;
  slide: number;
}

function makeConstraint(): Constraint {
  return {
    a: null as unknown as Body,
    b: null,
    s: null,
    key: 0,
    m: makeManifold(),
    friction: 0,
    restitution: 0,
    count: 0,
    rAx: [0, 0],
    rAz: [0, 0],
    rBx: [0, 0],
    rBz: [0, 0],
    normalMass: [0, 0],
    tangentMass: [0, 0],
    velBias: [0, 0],
    pn: [0, 0],
    pt: [0, 0],
    approach: 0,
    slide: 0,
  };
}

const ID_SPACE = 1 << 20;

/**
 * Deterministic 2D rigid body world. Iteration order depends only on creation order,
 * so identical inputs always produce identical results.
 */
export class PhysicsWorld {
  readonly config: PhysicsConfig;
  readonly bodies: Body[] = [];
  readonly statics: StaticCollider[] = [];
  readonly triggers: Trigger[] = [];
  /** Contacts aggregated over the last `step` call. */
  contacts: ContactEvent[] = [];
  /** Trigger enter/exit events from the last `step` call. */
  triggerEvents: TriggerEvent[] = [];
  readonly stats: PhysicsStats = { bodies: 0, awake: 0, statics: 0, pairsTested: 0, contacts: 0, stepMs: 0 };

  private nextBodyId = 1;
  private nextStaticId = 0;
  private nextTriggerId = 1;
  private readonly dynGrid: SpatialGrid<Body>;
  private readonly staticGrid: SpatialGrid<StaticCollider>;
  private readonly constraintPool: Constraint[] = [];
  private constraintCount = 0;
  private readonly stepContacts = new Map<number, ContactEvent>();
  private prevTouching = new Set<number>();
  private readonly scratchManifold = makeManifold();
  private readonly scratchGeom = makeGeom();
  private readonly rayHit: RayShapeHit = { t: 0, nx: 0, nz: 0 };

  constructor(config: Partial<PhysicsConfig> = {}) {
    this.config = { ...DEFAULT_PHYSICS_CONFIG, ...config };
    this.dynGrid = new SpatialGrid<Body>(this.config.cellSize);
    this.staticGrid = new SpatialGrid<StaticCollider>(this.config.cellSize);
  }

  // ---------------------------------------------------------------- bodies

  createBody(def: BodyDef): Body {
    const b = new Body(this.nextBodyId++, def);
    b.index = this.bodies.length;
    this.bodies.push(b);
    return b;
  }

  removeBody(body: Body): void {
    const i = this.bodies.indexOf(body);
    if (i < 0) return;
    this.bodies.splice(i, 1);
    for (let k = i; k < this.bodies.length; k++) (this.bodies[k] as Body).index = k;
    body.index = -1;
    body.enabled = false;
  }

  getBody(id: number): Body | undefined {
    for (const b of this.bodies) if (b.id === id) return b;
    return undefined;
  }

  // ---------------------------------------------------------------- statics

  addStatic(def: StaticDef): StaticCollider {
    let shape: Shape;
    let pos: V2;
    let angle = 0;
    if (def.kind === 'box') {
      shape = { type: 'box', hx: def.halfX, hz: def.halfZ };
      pos = { x: def.center.x, z: def.center.z };
      angle = def.angle ?? 0;
    } else if (def.kind === 'circle') {
      shape = { type: 'circle', r: def.radius };
      pos = { x: def.center.x, z: def.center.z };
    } else {
      const dx = def.b.x - def.a.x;
      const dz = def.b.z - def.a.z;
      const len = Math.sqrt(dx * dx + dz * dz);
      const t = def.thickness ?? 0.6;
      shape = { type: 'box', hx: t / 2, hz: len / 2 };
      pos = { x: (def.a.x + def.b.x) / 2, z: (def.a.z + def.b.z) / 2 };
      angle = Math.atan2(dx, dz);
    }
    const s = new StaticCollider(
      this.nextStaticId++,
      shape,
      pos,
      angle,
      def.blocksSight ?? true,
      def.friction ?? 0.3,
      def.restitution ?? 0.15,
      def.tag,
    );
    this.statics.push(s);
    this.staticGrid.insert(s, s.geom.aabb);
    return s;
  }

  removeStatic(s: StaticCollider): void {
    const i = this.statics.indexOf(s);
    if (i < 0) return;
    this.statics.splice(i, 1);
    this.staticGrid.remove(s, s.geom.aabb);
  }

  // ---------------------------------------------------------------- triggers

  addTrigger(def: TriggerDef): Trigger {
    const t = new Trigger(
      this.nextTriggerId++,
      def.shape,
      { x: def.pos.x, z: def.pos.z },
      def.angle ?? 0,
      def.mask ?? Layer.VEHICLE | Layer.PLAYER,
      def.tag,
    );
    this.triggers.push(t);
    return t;
  }

  removeTrigger(t: Trigger): void {
    const i = this.triggers.indexOf(t);
    if (i >= 0) this.triggers.splice(i, 1);
  }

  // ---------------------------------------------------------------- stepping

  step(dt: number): void {
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const cfg = this.config;
    const h = dt / cfg.substeps;
    this.stepContacts.clear();
    this.stats.pairsTested = 0;

    for (const b of this.bodies) {
      b.prevPos.x = b.pos.x;
      b.prevPos.z = b.pos.z;
      b.prevAngle = b.angle;
    }

    for (let sub = 0; sub < cfg.substeps; sub++) {
      this.integrateVelocities(h);
      this.rebuildGrid();
      this.findContacts();
      this.prepareConstraints();
      for (let it = 0; it < cfg.velocityIterations; it++) this.solveVelocities();
      this.accumulateContactEvents();
      this.correctPositions();
      this.integratePositions(h);
    }

    for (const b of this.bodies) {
      b.force.x = 0;
      b.force.z = 0;
      b.torque = 0;
    }
    this.rebuildGrid();
    this.updateSleep(dt);
    this.finishContacts();
    this.updateTriggers();

    this.stats.bodies = this.bodies.length;
    let awake = 0;
    for (const b of this.bodies) if (b.enabled && !b.sleeping) awake++;
    this.stats.awake = awake;
    this.stats.statics = this.statics.length;
    this.stats.contacts = this.contacts.length;
    this.stats.stepMs = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  }

  private integrateVelocities(h: number): void {
    for (const b of this.bodies) {
      if (!b.enabled || b.sleeping || b.type !== 'dynamic') continue;
      b.vel.x += b.force.x * b.invMass * h;
      b.vel.z += b.force.z * b.invMass * h;
      b.angVel += b.torque * b.invInertia * h;
      if (b.linearDamping > 0) {
        const f = 1 / (1 + h * b.linearDamping);
        b.vel.x *= f;
        b.vel.z *= f;
      }
      if (b.angularDamping > 0) b.angVel *= 1 / (1 + h * b.angularDamping);
    }
  }

  private integratePositions(h: number): void {
    for (const b of this.bodies) {
      if (!b.enabled || b.sleeping) continue;
      b.pos.x += b.vel.x * h;
      b.pos.z += b.vel.z * h;
      b.angle += b.angVel * h;
      if (b.angle > Math.PI) b.angle -= Math.PI * 2;
      else if (b.angle < -Math.PI) b.angle += Math.PI * 2;
    }
  }

  private rebuildGrid(): void {
    this.dynGrid.clear();
    for (const b of this.bodies) {
      if (!b.enabled) continue;
      computeGeom(b.geom, b.shape, b.pos, b.angle);
      this.dynGrid.insert(b, b.geom.aabb);
    }
  }

  private allocConstraint(): Constraint {
    let c = this.constraintPool[this.constraintCount];
    if (!c) {
      c = makeConstraint();
      this.constraintPool.push(c);
    }
    this.constraintCount++;
    return c;
  }

  private findContacts(): void {
    this.constraintCount = 0;
    for (const a of this.bodies) {
      if (!a.enabled) continue;
      const aActive = !a.sleeping && (a.type === 'dynamic' || a.vel.x !== 0 || a.vel.z !== 0 || a.angVel !== 0);
      if (!aActive) continue;
      // Body vs body
      this.dynGrid.query(a.geom.aabb, (b) => {
        if (b === a) return;
        const bActive = !b.sleeping && (b.type === 'dynamic' || b.vel.x !== 0 || b.vel.z !== 0 || b.angVel !== 0);
        // Active pairs are handled once, from the lower index.
        if (bActive && b.index < a.index) return;
        if (a.type === 'kinematic' && b.type === 'kinematic') return;
        if ((a.layer & b.mask) === 0 || (b.layer & a.mask) === 0) return;
        if (!aabbOverlap(a.geom, b.geom)) return;
        this.stats.pairsTested++;
        const c = this.allocConstraint();
        if (!collide(a.geom, b.geom, c.m)) {
          this.constraintCount--;
          return;
        }
        if (b.sleeping) b.wake();
        c.a = a;
        c.b = b;
        c.s = null;
        const lo = Math.min(a.id, b.id);
        const hi = Math.max(a.id, b.id);
        c.key = lo * ID_SPACE * 2 + hi;
        c.friction = Math.sqrt(a.friction * b.friction);
        c.restitution = Math.max(a.restitution, b.restitution);
      });
      if (a.type !== 'dynamic' || (a.mask & Layer.STATIC) === 0) continue;
      // Body vs static
      this.staticGrid.query(a.geom.aabb, (s) => {
        if (!aabbOverlap(a.geom, s.geom)) return;
        this.stats.pairsTested++;
        const c = this.allocConstraint();
        if (!collide(a.geom, s.geom, c.m)) {
          this.constraintCount--;
          return;
        }
        c.a = a;
        c.b = null;
        c.s = s;
        c.key = a.id * ID_SPACE * 2 + ID_SPACE + s.id;
        c.friction = Math.sqrt(a.friction * s.friction);
        c.restitution = Math.max(a.restitution, s.restitution);
      });
    }
  }

  private prepareConstraints(): void {
    const thr = this.config.restitutionThreshold;
    for (let i = 0; i < this.constraintCount; i++) {
      const c = this.constraintPool[i] as Constraint;
      const a = c.a;
      const b = c.b;
      const nx = c.m.nx;
      const nz = c.m.nz;
      const tx = -nz;
      const tz = nx;
      c.count = c.m.count;
      c.approach = 0;
      c.slide = 0;
      const imA = a.invMass;
      const iiA = a.invInertia;
      const imB = b ? b.invMass : 0;
      const iiB = b ? b.invInertia : 0;
      for (let p = 0; p < c.count; p++) {
        const pt = c.m.points[p] as { x: number; z: number };
        const rAx = pt.x - a.pos.x;
        const rAz = pt.z - a.pos.z;
        const rBx = b ? pt.x - b.pos.x : 0;
        const rBz = b ? pt.z - b.pos.z : 0;
        c.rAx[p] = rAx;
        c.rAz[p] = rAz;
        c.rBx[p] = rBx;
        c.rBz[p] = rBz;
        const rnA = rAz * nx - rAx * nz;
        const rnB = rBz * nx - rBx * nz;
        const kn = imA + imB + iiA * rnA * rnA + iiB * rnB * rnB;
        c.normalMass[p] = kn > 0 ? 1 / kn : 0;
        const rtA = rAz * tx - rAx * tz;
        const rtB = rBz * tx - rBx * tz;
        const kt = imA + imB + iiA * rtA * rtA + iiB * rtB * rtB;
        c.tangentMass[p] = kt > 0 ? 1 / kt : 0;
        c.pn[p] = 0;
        c.pt[p] = 0;
        // Relative normal velocity (B - A).
        const vn = this.relVel(c, p, nx, nz);
        c.velBias[p] = vn < -thr ? -c.restitution * vn : 0;
        if (-vn > c.approach) c.approach = -vn;
        const vt = Math.abs(this.relVel(c, p, tx, tz));
        if (vt > c.slide) c.slide = vt;
      }
    }
  }

  private relVel(c: Constraint, p: number, dx: number, dz: number): number {
    const a = c.a;
    const b = c.b;
    // v + w x r  with (w x r) = (w*r.z, -w*r.x)
    const vAx = a.vel.x + a.angVel * (c.rAz[p] as number);
    const vAz = a.vel.z - a.angVel * (c.rAx[p] as number);
    let vBx = 0;
    let vBz = 0;
    if (b) {
      vBx = b.vel.x + b.angVel * (c.rBz[p] as number);
      vBz = b.vel.z - b.angVel * (c.rBx[p] as number);
    }
    return (vBx - vAx) * dx + (vBz - vAz) * dz;
  }

  private applyImpulse(c: Constraint, p: number, px: number, pz: number): void {
    const a = c.a;
    const b = c.b;
    a.vel.x -= px * a.invMass;
    a.vel.z -= pz * a.invMass;
    a.angVel -= a.invInertia * ((c.rAz[p] as number) * px - (c.rAx[p] as number) * pz);
    if (b) {
      b.vel.x += px * b.invMass;
      b.vel.z += pz * b.invMass;
      b.angVel += b.invInertia * ((c.rBz[p] as number) * px - (c.rBx[p] as number) * pz);
    }
  }

  private solveVelocities(): void {
    for (let i = 0; i < this.constraintCount; i++) {
      const c = this.constraintPool[i] as Constraint;
      const nx = c.m.nx;
      const nz = c.m.nz;
      const tx = -nz;
      const tz = nx;
      // Friction first (uses current normal impulse as the bound).
      for (let p = 0; p < c.count; p++) {
        const vt = this.relVel(c, p, tx, tz);
        let lambda = -vt * (c.tangentMass[p] as number);
        const maxF = c.friction * (c.pn[p] as number);
        const old = c.pt[p] as number;
        const next = Math.max(-maxF, Math.min(old + lambda, maxF));
        lambda = next - old;
        c.pt[p] = next;
        this.applyImpulse(c, p, tx * lambda, tz * lambda);
      }
      for (let p = 0; p < c.count; p++) {
        const vn = this.relVel(c, p, nx, nz);
        let lambda = ((c.velBias[p] as number) - vn) * (c.normalMass[p] as number);
        const old = c.pn[p] as number;
        const next = Math.max(old + lambda, 0);
        lambda = next - old;
        c.pn[p] = next;
        this.applyImpulse(c, p, nx * lambda, nz * lambda);
      }
    }
  }

  private correctPositions(): void {
    const { slop, correction } = this.config;
    for (let i = 0; i < this.constraintCount; i++) {
      const c = this.constraintPool[i] as Constraint;
      let depth = 0;
      for (let p = 0; p < c.count; p++) depth = Math.max(depth, c.m.points[p]!.depth);
      const corr = Math.max(depth - slop, 0) * correction;
      if (corr <= 0) continue;
      const imA = c.a.invMass;
      const imB = c.b ? c.b.invMass : 0;
      const total = imA + imB;
      if (total <= 0) continue;
      const k = corr / total;
      c.a.pos.x -= c.m.nx * k * imA;
      c.a.pos.z -= c.m.nz * k * imA;
      if (c.b) {
        c.b.pos.x += c.m.nx * k * imB;
        c.b.pos.z += c.m.nz * k * imB;
      }
    }
  }

  private accumulateContactEvents(): void {
    for (let i = 0; i < this.constraintCount; i++) {
      const c = this.constraintPool[i] as Constraint;
      let impulse = 0;
      for (let p = 0; p < c.count; p++) impulse += c.pn[p] as number;
      let ev = this.stepContacts.get(c.key);
      if (!ev) {
        const pt = c.m.points[0];
        ev = {
          a: c.a.tag,
          b: c.b ? c.b.tag : c.s ? c.s.tag : null,
          impulse: 0,
          speed: 0,
          slide: 0,
          pos: { x: pt.x, z: pt.z },
          normal: { x: c.m.nx, z: c.m.nz },
          isNew: false,
        };
        this.stepContacts.set(c.key, ev);
      }
      ev.impulse += impulse;
      if (c.approach > ev.speed) ev.speed = c.approach;
      if (c.slide > ev.slide) ev.slide = c.slide;
    }
  }

  private finishContacts(): void {
    const touching = new Set<number>();
    const out: ContactEvent[] = [];
    for (const [key, ev] of this.stepContacts) {
      touching.add(key);
      ev.isNew = !this.prevTouching.has(key);
      out.push(ev);
    }
    this.prevTouching = touching;
    this.contacts = out;
  }

  private updateSleep(dt: number): void {
    const cfg = this.config;
    for (const b of this.bodies) {
      if (!b.enabled || b.sleeping || !b.canSleep || b.type !== 'dynamic') continue;
      if (Math.abs(b.vel.x) < cfg.sleepLinear && Math.abs(b.vel.z) < cfg.sleepLinear && Math.abs(b.angVel) < cfg.sleepAngular) {
        b.sleepTimer += dt;
        if (b.sleepTimer >= cfg.sleepTime) {
          b.sleeping = true;
          b.vel.x = 0;
          b.vel.z = 0;
          b.angVel = 0;
        }
      } else {
        b.sleepTimer = 0;
      }
    }
  }

  private updateTriggers(): void {
    const events: TriggerEvent[] = [];
    const m = this.scratchManifold;
    for (const t of this.triggers) {
      const now = new Map<number, BodyTagData>();
      this.dynGrid.query(t.geom.aabb, (b) => {
        if (!b.enabled || (b.layer & t.mask) === 0) return;
        if (!aabbOverlap(t.geom, b.geom)) return;
        if (collide(t.geom, b.geom, m)) now.set(b.id, b.tag);
      });
      for (const [id, tag] of now) {
        if (!t.inside.has(id)) events.push({ type: 'enter', triggerId: t.id, triggerTag: t.tag, body: tag });
      }
      for (const [id, tag] of t.inside) {
        if (!now.has(id)) events.push({ type: 'exit', triggerId: t.id, triggerTag: t.tag, body: tag });
      }
      t.inside.clear();
      for (const [id, tag] of now) t.inside.set(id, tag);
    }
    this.triggerEvents = events;
  }

  // ---------------------------------------------------------------- queries

  /**
   * Cast a ray. `dir` need not be normalised. Returns the closest hit within maxDist or null.
   * Walks the broadphase grid cell by cell (Amanatides–Woo), so long rays stay cheap.
   */
  raycast(origin: V2, dir: V2, maxDist: number, opts: RaycastOptions = {}): RayHit | null {
    const len = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
    if (len < 1e-9 || maxDist <= 0) return null;
    const d = { x: dir.x / len, z: dir.z / len };
    const mask = opts.mask ?? Layer.ALL;
    const includeStatics = opts.includeStatics ?? true;
    const sightOnly = opts.sightOnly ?? false;
    const ignoreId = opts.ignore ? opts.ignore.id : -1;
    const cs = this.config.cellSize;

    let cx = Math.floor(origin.x / cs);
    let cz = Math.floor(origin.z / cs);
    const stepX = d.x > 0 ? 1 : d.x < 0 ? -1 : 0;
    const stepZ = d.z > 0 ? 1 : d.z < 0 ? -1 : 0;
    const tDeltaX = stepX !== 0 ? Math.abs(cs / d.x) : Infinity;
    const tDeltaZ = stepZ !== 0 ? Math.abs(cs / d.z) : Infinity;
    let tMaxX = stepX > 0 ? ((cx + 1) * cs - origin.x) / d.x : stepX < 0 ? (cx * cs - origin.x) / d.x : Infinity;
    let tMaxZ = stepZ > 0 ? ((cz + 1) * cs - origin.z) / d.z : stepZ < 0 ? (cz * cs - origin.z) / d.z : Infinity;

    const sStamp = this.staticGrid.nextStamp();
    const bStamp = this.dynGrid.nextStamp();
    let best = maxDist;
    let hit: RayHit | null = null;
    const rh = this.rayHit;
    const maxCells = Math.ceil(maxDist / cs) * 2 + 4;

    for (let iter = 0; iter < maxCells; iter++) {
      if (includeStatics) {
        const list = this.staticGrid.cell(cx, cz);
        if (list) {
          for (const s of list) {
            if (s.stamp === sStamp) continue;
            s.stamp = sStamp;
            if (sightOnly && !s.blocksSight) continue;
            if (rayShape(origin, d, best, s.shape, s.pos, s.angle, rh) && rh.t <= best) {
              best = rh.t;
              hit = {
                point: { x: origin.x + d.x * rh.t, z: origin.z + d.z * rh.t },
                normal: { x: rh.nx, z: rh.nz },
                distance: rh.t,
                bodyId: null,
                staticId: s.id,
                tag: s.tag,
                label: s.label ?? null,
              };
            }
          }
        }
      }
      if (mask !== 0) {
        const list = this.dynGrid.cell(cx, cz);
        if (list) {
          for (const b of list) {
            if (b.stamp === bStamp) continue;
            b.stamp = bStamp;
            if (b.id === ignoreId || !b.enabled || (b.layer & mask) === 0) continue;
            if (rayShape(origin, d, best, b.shape, b.pos, b.angle, rh) && rh.t <= best) {
              best = rh.t;
              hit = {
                point: { x: origin.x + d.x * rh.t, z: origin.z + d.z * rh.t },
                normal: { x: rh.nx, z: rh.nz },
                distance: rh.t,
                bodyId: b.id,
                staticId: null,
                tag: b.tag,
                label: null,
              };
            }
          }
        }
      }
      const tExit = Math.min(tMaxX, tMaxZ);
      if (best <= tExit || tExit > maxDist) break;
      if (tMaxX < tMaxZ) {
        cx += stepX;
        tMaxX += tDeltaX;
      } else {
        cz += stepZ;
        tMaxZ += tDeltaZ;
      }
    }
    return hit;
  }

  /** True when no sight-blocking static geometry lies between a and b. */
  lineOfSight(a: V2, b: V2, opts: RaycastOptions = {}): boolean {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 1e-6) return true;
    const hit = this.raycast(a, { x: dx, z: dz }, dist, { mask: 0, sightOnly: true, ...opts });
    return hit === null;
  }

  /** Dynamic bodies whose shape overlaps the circle. */
  queryCircle(center: V2, radius: number, mask: number = Layer.ALL, out: Body[] = []): Body[] {
    const g = this.scratchGeom;
    computeGeom(g, { type: 'circle', r: radius }, center, 0);
    const m = this.scratchManifold;
    this.dynGrid.query(g.aabb, (b) => {
      if (!b.enabled || (b.layer & mask) === 0) return;
      if (!aabbOverlap(g, b.geom)) return;
      if (collide(g, b.geom, m)) out.push(b);
    });
    return out;
  }

  /** Dynamic bodies whose AABB overlaps the rect. */
  queryRect(r: Rect, mask: number = Layer.ALL, out: Body[] = []): Body[] {
    this.dynGrid.query(r, (b) => {
      if (!b.enabled || (b.layer & mask) === 0) return;
      const a = b.geom.aabb;
      if (a.minX <= r.maxX && a.maxX >= r.minX && a.minZ <= r.maxZ && a.maxZ >= r.minZ) out.push(b);
    });
    return out;
  }

  /** Static colliders overlapping the circle. */
  queryStaticsCircle(center: V2, radius: number, out: StaticCollider[] = []): StaticCollider[] {
    const g = this.scratchGeom;
    computeGeom(g, { type: 'circle', r: radius }, center, 0);
    const m = this.scratchManifold;
    this.staticGrid.query(g.aabb, (s) => {
      if (aabbOverlap(g, s.geom) && collide(g, s.geom, m)) out.push(s);
    });
    return out;
  }

  /** True if a shape at (pos, angle) overlaps no statics and no bodies in `mask`. Use before spawning. */
  isShapeFree(shape: Shape, pos: V2, angle = 0, mask: number = Layer.ALL, ignore: Body | null = null): boolean {
    const g = makeGeom();
    computeGeom(g, shape, pos, angle);
    const m = this.scratchManifold;
    let free = true;
    this.staticGrid.query(g.aabb, (s) => {
      if (free && aabbOverlap(g, s.geom) && collide(g, s.geom, m)) free = false;
    });
    if (!free) return false;
    this.dynGrid.query(g.aabb, (b) => {
      if (!free || b === ignore || !b.enabled || (b.layer & mask) === 0) return;
      if (aabbOverlap(g, b.geom) && collide(g, b.geom, m)) free = false;
    });
    return free;
  }
}
